import { supabase } from '../lib/supabase';

export const attachmentService = {
  /**
   * Upload a file to negotiation documents bucket
   * @param {File} file - The file to upload
   * @param {string} negotiationId - The negotiation ID
   * @param {string} documentType - Type of document ('invoice' or 'tax_document')
   * @returns {Promise<string>} - The file path in storage
   */
  async uploadNegotiationDocument(file, negotiationId, documentType) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      
      if (!user) {
        throw new Error('Usuário não autenticado');
      }

      // Generate unique file path
      const timestamp = Date.now();
      const fileExtension = file?.name?.split('.')?.pop();
      const fileName = `${documentType}_${timestamp}.${fileExtension}`;
      const filePath = `${user?.id}/${negotiationId}/${fileName}`;

      // Upload file to storage
      const { data: uploadData, error: uploadError } = await supabase?.storage?.from('negotiation-documents')?.upload(filePath, file);

      if (uploadError) {
        throw uploadError;
      }

      // Create attachment record in database
      const { data: attachmentData, error: attachmentError } = await supabase?.from('attachments')?.insert([
          {
            entity_id: negotiationId,
            entity_type: 'negotiation',
            document_type: documentType,
            file_name: file?.name,
            file_size: file?.size,
            file_url: uploadData?.path,
            uploaded_by: user?.id
          }
        ])?.select()?.single();

      if (attachmentError) {
        // If database insert fails, delete the uploaded file
        await supabase?.storage?.from('negotiation-documents')?.remove([filePath]);
        throw attachmentError;
      }

      return attachmentData;
    } catch (error) {
      console.error('Error uploading document:', error);
      throw new Error(error.message || 'Erro ao fazer upload do documento');
    }
  },

  /**
   * Get signed URL for a private document
   * @param {string} filePath - The file path in storage
   * @param {number} expiresIn - Expiry time in seconds (default 3600 = 1 hour)
   * @returns {Promise<string>} - The signed URL
   */
  async getSignedUrl(filePath, expiresIn = 3600) {
    try {
      const { data, error } = await supabase?.storage?.from('negotiation-documents')?.createSignedUrl(filePath, expiresIn);

      if (error) {
        throw error;
      }

      return data?.signedUrl;
    } catch (error) {
      console.error('Error getting signed URL:', error);
      throw new Error(error.message || 'Erro ao obter URL do documento');
    }
  },

  /**
   * Get all attachments for a negotiation
   * @param {string} negotiationId - The negotiation ID
   * @returns {Promise<Array>} - Array of attachments with signed URLs
   */
  async getNegotiationAttachments(negotiationId) {
    try {
      const { data: attachments, error } = await supabase?.from('attachments')?.select('*')?.eq('entity_id', negotiationId)?.eq('entity_type', 'negotiation')?.order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      // Get signed URLs for all attachments
      const attachmentsWithUrls = await Promise.all(
        attachments?.map(async (attachment) => {
          try {
            const signedUrl = await this.getSignedUrl(attachment?.file_url);
            return {
              ...attachment,
              signedUrl
            };
          } catch (err) {
            console.error('Error getting signed URL for attachment:', err);
            return {
              ...attachment,
              signedUrl: null
            };
          }
        })
      );

      return attachmentsWithUrls;
    } catch (error) {
      console.error('Error fetching negotiation attachments:', error);
      throw new Error(error.message || 'Erro ao buscar anexos');
    }
  },

  /**
   * Delete an attachment
   * @param {string} attachmentId - The attachment ID
   * @returns {Promise<void>}
   */
  async deleteAttachment(attachmentId) {
    try {
      // Get attachment details first
      const { data: attachment, error: fetchError } = await supabase?.from('attachments')?.select('file_url')?.eq('id', attachmentId)?.single();

      if (fetchError) {
        throw fetchError;
      }

      // Delete file from storage
      const { error: storageError } = await supabase?.storage?.from('negotiation-documents')?.remove([attachment?.file_url]);

      if (storageError) {
        console.error('Error deleting file from storage:', storageError);
      }

      // Delete attachment record from database
      const { error: deleteError } = await supabase?.from('attachments')?.delete()?.eq('id', attachmentId);

      if (deleteError) {
        throw deleteError;
      }
    } catch (error) {
      console.error('Error deleting attachment:', error);
      throw new Error(error.message || 'Erro ao deletar anexo');
    }
  }
};